import { computed, inject, Injectable, signal } from '@angular/core';
import { parse } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { Schedule } from '../models/schedule.model';
import { ContractorsService } from '../services/contractors.service';
import { SchedulesService } from '../services/schedules.service';
import { selectedDayFormat } from '../utility/time';

export interface ShiftDraftSnapshot {
  contractorGuid: string | null;
  dateKey: string;
  date: Date;
  userGuid: string;
  userName: string;
  siteGuid: string | null;
  siteName: string;
  start: Date | null;
  end: Date | null;
  infoText: string;
  project: string;
  status: 'draft' | 'published';
  absence: string | null;
}

export interface ShiftDraftChangeRecord {
  key: string;
  type: 'added' | 'updated' | 'removed';
  before: ShiftDraftSnapshot | null;
  after: ShiftDraftSnapshot | null;
}

@Injectable({
  providedIn: 'root',
})
export class ShiftDraftReviewService {
  private contractorsService = inject(ContractorsService);
  private schedulesService = inject(SchedulesService);

  private records = signal<Record<string, ShiftDraftChangeRecord>>({});

  changeRecords = computed(() => Object.values(this.records()));
  hasPendingChanges = computed(() => this.changeRecords().length > 0);

  getChangeKey(userGuid: string, dateKey: string, siteGuid: string | null): string {
    return `${userGuid}::${dateKey}::${siteGuid ?? 'none'}`;
  }

  recordAddition(after: ShiftDraftSnapshot): void {
    const key = this.getChangeKey(after.userGuid, after.dateKey, after.siteGuid);
    const current = this.records()[key];

    if (current?.type === 'removed' && current.before) {
      if (this.isSameSnapshot(current.before, after)) {
        this.removeRecord(key);
        return;
      }
      this.setRecord(key, {
        key,
        type: 'updated',
        before: current.before,
        after,
      });
      return;
    }

    if (current?.type === 'added') {
      this.setRecord(key, { ...current, after });
      return;
    }

    if (current?.type === 'updated') {
      this.setRecord(key, { ...current, after });
      return;
    }

    this.setRecord(key, {
      key,
      type: 'added',
      before: null,
      after,
    });
  }

  recordUpdate(before: ShiftDraftSnapshot, after: ShiftDraftSnapshot): void {
    const key = this.getChangeKey(after.userGuid, after.dateKey, after.siteGuid);
    const current = this.records()[key];

    if (current?.type === 'added') {
      this.setRecord(key, {
        ...current,
        after,
      });
      return;
    }

    if (this.isSameSnapshot(before, after)) {
      if (current?.type === 'updated') {
        this.removeRecord(key);
      }
      return;
    }

    this.setRecord(key, {
      key,
      type: 'updated',
      before: current?.before ?? before,
      after,
    });
  }

  recordRemoval(before: ShiftDraftSnapshot): void {
    const key = this.getChangeKey(before.userGuid, before.dateKey, before.siteGuid);
    const current = this.records()[key];

    if (current?.type === 'added') {
      this.removeRecord(key);
      return;
    }

    if (current?.type === 'updated' && current.before) {
      this.setRecord(key, {
        key,
        type: 'removed',
        before: current.before,
        after: null,
      });
      return;
    }

    this.setRecord(key, {
      key,
      type: 'removed',
      before,
      after: null,
    });
  }

  async publishPendingChanges(): Promise<void> {
    const contractorGuid = this.contractorsService.contractorS()?.guid ?? null;
    if (!contractorGuid) {
      return;
    }

    const schedules = await firstValueFrom(this.schedulesService.getListOnce({ contractor: contractorGuid }));
    const draftSchedules = schedules.filter((schedule) => schedule.times.some((time) => time.status === 'draft'));

    await Promise.all(draftSchedules.map(async (schedule) => {
      schedule.times = schedule.times.map((time) => ({
        ...time,
        status: time.status === 'draft' ? 'published' : time.status,
      }));
      await this.schedulesService.updateOnly(schedule.guid, { times: schedule.times });
    }));

    this.records.set({});
  }

  async discardPendingChanges(): Promise<void> {
    const changes = [...this.changeRecords()];

    for (const change of changes) {
      switch (change.type) {
        case 'added':
          if (change.after) {
            await this.removeSnapshot(change.after);
          }
          break;
        case 'updated':
          if (change.before) {
            await this.restoreSnapshot(change.before);
          }
          break;
        case 'removed':
          if (change.before) {
            await this.restoreSnapshot(change.before);
          }
          break;
      }
    }

    this.records.set({});
  }

  clear(): void {
    this.records.set({});
  }

  private setRecord(key: string, record: ShiftDraftChangeRecord): void {
    this.records.update((items) => ({
      ...items,
      [key]: record,
    }));
  }

  private removeRecord(key: string): void {
    this.records.update((items) => {
      return Object.fromEntries(
        Object.entries(items).filter(([itemKey]) => itemKey !== key),
      );
    });
  }

  private async removeSnapshot(snapshot: ShiftDraftSnapshot): Promise<void> {
    if (!snapshot.siteGuid) {
      return;
    }

    const schedule = await firstValueFrom(this.schedulesService.getSingleOnce({
      site: snapshot.siteGuid,
      date: snapshot.dateKey,
    }));

    if (!schedule) {
      return;
    }

    const updated = this.schedulesService.removeUser(schedule, snapshot.userGuid);
    updated.absence = updated.times.some((time) => time.absence != null);
    await this.schedulesService.update(updated);
  }

  private async restoreSnapshot(snapshot: ShiftDraftSnapshot): Promise<void> {
    if (!snapshot.siteGuid) {
      return;
    }

    let schedule = await firstValueFrom(this.schedulesService.getSingleOnce({
      site: snapshot.siteGuid,
      date: snapshot.dateKey,
    }));

    if (!schedule) {
      schedule = this.createEmptySchedule(snapshot);
    }

    const currentUsers = schedule.users ?? [];
    schedule.users = Array.from(new Set([...currentUsers, snapshot.userGuid]));
    schedule.information = schedule.information ?? [];
    schedule.projects = schedule.projects ?? [];
    schedule.times = schedule.times ?? [];

    const existingTimeIndex = schedule.times.findIndex((time) => time.user === snapshot.userGuid);
    const restoredTime = {
      user: snapshot.userGuid,
      start: snapshot.start,
      end: snapshot.end,
      absence: snapshot.absence,
      status: snapshot.status,
      machine: null,
      repeatGuid: null,
    };

    if (existingTimeIndex >= 0) {
      schedule.times[existingTimeIndex] = restoredTime;
    } else {
      schedule.times.push(restoredTime);
    }

    const infoIndex = schedule.information.findIndex((info) => info.user === snapshot.userGuid);
    if (snapshot.infoText) {
      const information = { user: snapshot.userGuid, username: snapshot.userName, text: snapshot.infoText };
      if (infoIndex >= 0) {
        schedule.information[infoIndex] = information;
      } else {
        schedule.information.push(information);
      }
    } else if (infoIndex >= 0) {
      schedule.information.splice(infoIndex, 1);
    }

    const projectIndex = schedule.projects.findIndex((project) => project.user === snapshot.userGuid);
    if (snapshot.project) {
      const project = { user: snapshot.userGuid, project: snapshot.project };
      if (projectIndex >= 0) {
        schedule.projects[projectIndex] = project;
      } else {
        schedule.projects.push(project);
      }
    } else if (projectIndex >= 0) {
      schedule.projects.splice(projectIndex, 1);
    }

    schedule.absence = schedule.times.some((time) => time.absence != null);

    if (schedule.guid) {
      await this.schedulesService.update(schedule);
      return;
    }

    await this.schedulesService.save(schedule);
  }

  private createEmptySchedule(snapshot: ShiftDraftSnapshot): Schedule {
    return {
      contractor: snapshot.contractorGuid,
      date: snapshot.dateKey,
      dateObj: parse(snapshot.dateKey, selectedDayFormat, new Date()),
      site: snapshot.siteGuid,
      users: [],
      locked: [],
      information: [],
      machines: [],
      machinesGuids: [],
      projects: [],
      times: [],
      absence: false,
    };
  }

  private isSameSnapshot(left: ShiftDraftSnapshot, right: ShiftDraftSnapshot): boolean {
    return left.siteGuid === right.siteGuid
      && left.dateKey === right.dateKey
      && left.userGuid === right.userGuid
      && left.start?.getTime() === right.start?.getTime()
      && left.end?.getTime() === right.end?.getTime()
      && left.infoText === right.infoText
      && left.project === right.project
      && left.absence === right.absence;
  }
}
